import dynamic from 'next/dynamic';
import { type FC, useEffect, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import PlayButton from 'molecules/Button/PlayButton';
import Image from 'molecules/Image';
import { VideoCustomConfigProps } from 'molecules/VideoCustom/config';

import { analyticsClient } from 'utils/analytics/client';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface VideoCustomProps extends VideoCustomConfigProps {
  className?: string;
  key?: string | number;
  priority?: boolean;
  isActive?: boolean;
  onPlay?: () => void;
}

const isValidVideoUrl = (url?: string) => {
  if (!url) {
    return false;
  }

  return /^https?:\/\//.test(url);
};

const VideoCustom: FC<VideoCustomProps> = ({ videoSrc, thumbnail, className, priority, isActive = true, onPlay }) => {
  const [playing, setPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const validUrl = isValidVideoUrl(videoSrc);

  useEffect(() => {
    setPlaying(false);
    setLoaded(false);
  }, [videoSrc]);

  useEffect(() => {
    if (!isActive) {
      setPlaying(false);
    }
  }, [isActive]);

  useEffect(() => {
    if (!playing) {
      return;
    }

    analyticsClient.track(`Viewed Video - ${thumbnail?.alt ?? document.title}`, {});
  }, [playing, thumbnail?.alt]);

  const playAriaLabel = thumbnail?.alt ? `Play video: ${thumbnail.alt}` : 'Play video';

  const thumbWidth = thumbnail?.file?.width;
  const thumbHeight = thumbnail?.file?.height;
  const thumbnailAspect = thumbWidth && thumbHeight ? `${thumbWidth} / ${thumbHeight}` : '16 / 9';
  const aspectRatio = playing ? '16 / 9' : thumbnailAspect;

  return (
    <div
      className={twJoin('relative mx-auto w-full max-w-[800px] overflow-hidden rounded-lg', className)}
      style={{ aspectRatio }}
      onMouseEnter={() => setLoaded(true)}
    >
      {!playing && (
        <div className="z-10 absolute-cover">
          {thumbnail ? (
            <Image
              {...(thumbnail.file || thumbnail)}
              className="size-full"
              alt={thumbnail.alt || ''}
              priority={priority}
              objectCover
              unsetRatio
              unsetMaxW
            />
          ) : (
            <div className="size-full bg-primary-50 dark:bg-primary-700" />
          )}

          <div className="flex items-center justify-center absolute-cover">
            <PlayButton
              onClick={() => {
                setPlaying(true);
                onPlay?.();
              }}
              ariaLabel={playAriaLabel}
            />
          </div>
        </div>
      )}

      {loaded && validUrl && playing && (
        <ReactPlayer
          key={videoSrc}
          url={videoSrc}
          controls
          playing
          muted={false}
          width="100%"
          height="100%"
          className="absolute-cover"
        />
      )}
    </div>
  );
};

export default VideoCustom;
